import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Form, Spinner, Alert } from "react-bootstrap";
import ListingCalendarService from "../../services/listing-calendar-service";

/**
 * Props:
 *  - show
 *  - onHide
 *  - email
 *  - issuerCode
 *  - onConfirm(otp)
 *  - initialCooldown (seconds)  <-- optional, default 120
 *  - initialError (string)      <-- optional, show as danger alert when modal opens
 *  - initialInfo (string)       <-- optional, show as success/info alert when modal opens
 */
const OtpModal = ({ show, onHide, email, issuerCode, onConfirm, initialCooldown = 120, initialError = '', initialInfo = '' }) => {
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");

  // resend/cooldown state
  const [cooldown, setCooldown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);

  // server lock state based on attempts (when attempts reaches max)
  const [serverAttempts, setServerAttempts] = useState(0);
  const MAX_ATTEMPTS = 3; // keep in sync with your lambda env OTP_MAX_ATTEMPTS

  // refs for accurate timing (prevents throttling/out-of-sync)
  const endTimeRef = useRef(null);          // timestamp in ms when cooldown ends
  const tickIntervalRef = useRef(null);     // interval id for ticking
  const listingCalendarService = new ListingCalendarService();

  useEffect(() => {
    // clear input/error each time modal opens/closes
    if (!show) {
      setOtp("");
      setError("");
      setLoading(false);
      setResendLoading(false);
      setInfoMessage("");
      setCooldown(0);
      setServerAttempts(0); // reset on close

      // clear any running tick
      if (tickIntervalRef.current) {
        clearInterval(tickIntervalRef.current);
        tickIntervalRef.current = null;
      }
      endTimeRef.current = null;
    } else {
      // When opening, apply any initial props from parent
      setError(initialError || "");
      setInfoMessage(initialInfo || "");

      const start = (typeof initialCooldown === 'number' && initialCooldown >= 0) ? initialCooldown : 120;
      startCooldown(start);
      setServerAttempts(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, initialCooldown, initialError, initialInfo]);

  // start a cooldown for `seconds` seconds (replaces previous cooldown logic)
  const startCooldown = (seconds) => {
    // sanitize input
    const s = Math.max(0, Math.floor(Number(seconds) || 0));
    if (s === 0) {
      // clear existing
      if (tickIntervalRef.current) {
        clearInterval(tickIntervalRef.current);
        tickIntervalRef.current = null;
      }
      endTimeRef.current = null;
      setCooldown(0);
      // optionally remove persisted key here if using persistence
      return;
    }

    // compute end timestamp
    const now = Date.now();
    const end = now + s * 1000;
    endTimeRef.current = end;

    // optional: persist across remounts (uncomment if desired)
    // try { sessionStorage.setItem(`otp_end_${email}_${issuerCode}`, String(end)); } catch(e){}

    // set immediate visible state
    setCooldown(s);

    // clear old interval if any
    if (tickIntervalRef.current) {
      clearInterval(tickIntervalRef.current);
      tickIntervalRef.current = null;
    }

    // tick every 500ms to remain responsive after tab is visible again.
    tickIntervalRef.current = setInterval(() => {
      if (!endTimeRef.current) {
        setCooldown(0);
        if (tickIntervalRef.current) { clearInterval(tickIntervalRef.current); tickIntervalRef.current = null; }
        return;
      }
      const remainingMs = endTimeRef.current - Date.now();
      const remainingSec = Math.max(0, Math.ceil(remainingMs / 1000));
      setCooldown(remainingSec);
      if (remainingSec <= 0) {
        // done
        if (tickIntervalRef.current) { clearInterval(tickIntervalRef.current); tickIntervalRef.current = null; }
        endTimeRef.current = null;
        // optional: remove persisted key
        // try { sessionStorage.removeItem(`otp_end_${email}_${issuerCode}`); } catch(e){}
      }
    }, 500);
  };

  // update countdown immediately when tab becomes visible (helps with throttling)
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible" && endTimeRef.current) {
        const remaining = Math.max(0, Math.ceil((endTimeRef.current - Date.now()) / 1000));
        setCooldown(remaining);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  // If I want to persist cooldown across remounts, uncomment this - Iant
  /*
  useEffect(() => {
    if (!show) return;
    try {
      const raw = sessionStorage.getItem(`otp_end_${email}_${issuerCode}`);
      if (raw) {
        const end = Number(raw);
        if (!Number.isNaN(end) && end > Date.now()) {
          endTimeRef.current = end;
          const remaining = Math.max(0, Math.ceil((end - Date.now())/1000));
          setCooldown(remaining);
          if (!tickIntervalRef.current) {
            // start ticking same as startCooldown but without resetting endTimeRef
            tickIntervalRef.current = setInterval(() => {
              const remainingMs = endTimeRef.current - Date.now();
              const remainingSec = Math.max(0, Math.ceil(remainingMs / 1000));
              setCooldown(remainingSec);
              if (remainingSec <= 0) {
                clearInterval(tickIntervalRef.current);
                tickIntervalRef.current = null;
                endTimeRef.current = null;
                sessionStorage.removeItem(`otp_end_${email}_${issuerCode}`);
              }
            }, 500);
          }
        }
      }
    } catch(e) {}
  }, [show, email, issuerCode]);
  */

  /**
   * Extracts message and attempts from an axios error object (robust).
   * Returns { message: string, attempts: number|null, errorCode: string|null }
   */
  const extractErrorInfo = (err) => {
    let attempts = null;
    let errorCode = null;
    let message = "";

    const resp = err && err.response && err.response.data;
    if (resp !== undefined && resp !== null) {
      // If response is string, try parse JSON
      if (typeof resp === "string") {
        try {
          const parsed = JSON.parse(resp);
          if (parsed) {
            if (parsed.message) message = parsed.message;
            else if (parsed.error) message = parsed.error;
            else message = resp;
            if (parsed.attempts !== undefined) attempts = Number(parsed.attempts) || null;
            if (parsed.error) errorCode = parsed.error;
          } else {
            message = resp;
          }
        } catch {
          // not JSON
          message = resp;
        }
      } else if (typeof resp === "object") {
        message = resp.message || resp.error || resp.aws_error_message || resp.detail || JSON.stringify(resp);
        if (resp.attempts !== undefined) {
          // handle DynamoDB numeric string or number
          attempts = Number(resp.attempts);
          if (Number.isNaN(attempts)) attempts = null;
        }
        if (resp.error) errorCode = resp.error;
      }
    }

    // fallback to http status special-cases
    if (!message && err && err.response && err.response.status) {
      if (err.response.status === 410) message = "OTP expired";
      else message = err.response.statusText || `Request failed with status ${err.response.status}`;
    }

    if (!message && err && err.message) message = err.message;

    return { message: String(message || ""), attempts, errorCode: errorCode || null };
  };

  const handleVerify = async () => {
    // enforce exact 6-digit numeric OTP client-side
    if (!otp || otp.length !== 6) {
      setError("Please enter the 6-digit numeric OTP.");
      return;
    }

    setLoading(true);
    setError("");
    setInfoMessage("");

    try {
      await listingCalendarService.verifyOTP(email, issuerCode, otp);

      // success -> clear attempts lock and notify parent
      setServerAttempts(0);
      if (typeof onConfirm === "function") {
        onConfirm(otp);
      }

      onHide();
    } catch (err) {
      console.error("OTP verify error:", err);
      const { message, attempts } = extractErrorInfo(err);

      // If attempts provided by server, persist it
      if (attempts !== null && !Number.isNaN(attempts)) {
        setServerAttempts(attempts);
      }

      setError(message || "Invalid OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mm = Math.floor(seconds / 60);
    const ss = seconds % 60;
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(mm)}:${pad(ss)}`;
  };

  const handleResend = async () => {
    if (cooldown > 0 || resendLoading) return;

    setResendLoading(true);
    setError("");
    setInfoMessage("");

    try {
      const res = await listingCalendarService.requestOTP(email, issuerCode);
      // If backend returns remaining_seconds, use it
      const backendCooldown = (res && res.data && (res.data.remaining_seconds || res.data.cooldownSeconds)) || null;
      // use startCooldown which sets an accurate deadline based on Date.now()
      startCooldown(backendCooldown || initialCooldown || 120);
      setInfoMessage("OTP resent. Please check your email.");
      // reset server attempt lock on resend (user got a new OTP)
      setServerAttempts(0);
    } catch (err) {
      console.error("Resend OTP error:", err);

      // prefer response body extraction here too
      const data = err && (err.response && err.response.data) ? err.response.data : null;
      if (data && (data.error && /otp already exists/i.test(String(data.error))) && (data.remaining_seconds || data.remaining_seconds === 0)) {
        // backend says otp already exists — use that remaining_seconds
        const remaining = Number(data.remaining_seconds) || 0;
        startCooldown(remaining);
        setError("An OTP already exists. Please use the existing code or wait for the cooldown.");
      } else {
        // also extract attempts if present in resend error
        const parsed = extractErrorInfo(err);
        if (parsed.attempts !== null) {
          setServerAttempts(parsed.attempts);
        }
        setError(parsed.message || "Failed to resend OTP. Please try again later.");
      }
    } finally {
      setResendLoading(false);
    }
  };

  const handleOtpChange = (e) => {
    const raw = e.target.value || "";
    // strip all non-digits
    const digits = raw.replace(/\D/g, "");
    // enforce max length 6
    const trimmed = digits.slice(0, 6);
    setOtp(trimmed);
    // clear inline error once input looks valid-ish
    if (trimmed.length === 6) {
      setError("");
    }
  };

  // prevent pasted non-digit content
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const clipboard = (e.clipboardData || window.clipboardData);
    const pasted = clipboard.getData("Text") || "";
    const digits = (pasted || "").replace(/\D/g, "").slice(0, 6);
    setOtp(digits);
    if (digits.length === 6) {
      setError("");
    }
  };

  // locked if server reports attempts at or above max
  const serverLocked = (serverAttempts >= MAX_ATTEMPTS);

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Verify OTP</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error ? <Alert variant="danger">{error}</Alert> : null}
        {infoMessage ? <Alert variant="success">{infoMessage}</Alert> : null}

        <Form.Group className="mb-3">
          <Form.Label>Enter the 6-digit OTP sent to {email || 'your email'}</Form.Label>
          <Form.Control
            type="text"
            inputMode="numeric"
            pattern="\d{6}"
            autoComplete="one-time-code"
            value={otp}
            onChange={handleOtpChange}
            onPaste={handleOtpPaste}
            placeholder="123456"
            disabled={loading || serverLocked}
            maxLength={6}
            aria-label="6-digit one time password"
          />
          <Form.Text muted>
            Only numbers, exactly 6 digits.
          </Form.Text>
        </Form.Group>

        <div className="d-flex justify-content-between align-items-center" style={{ marginTop: 8 }}>
          <small className="text-muted">Didn't receive it?</small>

          <div>
            <Button
              variant="link"
              size="sm"
              onClick={handleResend}
              disabled={cooldown > 0 || resendLoading}
              aria-disabled={cooldown > 0 || resendLoading}
            >
              {resendLoading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Resending...
                </>
              ) : cooldown > 0 ? (
                `Resend OTP (${formatTime(cooldown)})`
              ) : (
                'Resend OTP'
              )}
            </Button>
          </div>
        </div>

        <div style={{ marginTop: 6, fontSize: 12 }}>
          <small className="text-muted">
            You can resend again after the cooldown. (Cooldown: {formatTime(initialCooldown)})
          </small>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading || resendLoading}>
          Cancel
        </Button>

        <Button
          variant="primary"
          onClick={handleVerify}
          disabled={loading || otp.trim().length !== 6 || serverLocked}
          title={serverLocked ? `Locked due to ${serverAttempts}/${MAX_ATTEMPTS} attempts — request a new OTP` : undefined}
        >
          {loading ? <Spinner animation="border" size="sm" /> : (serverLocked ? `Locked (${serverAttempts}/${MAX_ATTEMPTS})` : "Verify")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OtpModal;
